/**
 * useContextMenu.ts - useContextMenu - Hook for managing context menu state
 * 
 * CHANGE HISTORY:
 * - 251101_0845 [pending] danielbeer: Added TypeScript generics for proper type safety
 * - 251021_0421 [7529d5e] danielbeer: feat: migrate all packages to TypeScript structure
 */
import { useState, useCallback } from 'react';
import type React from 'react';

export interface ContextMenuState<T = any> {
  isOpen: boolean;
  position: { x: number; y: number } | null;
  data: T | null;
}

export const useContextMenu = <T = any>() => {
  const [contextMenu, setContextMenu] = useState<ContextMenuState<T>>({
    isOpen: false,
    position: null,
    data: null,
  });

  /**
   * Open context menu at position
   * @param event - The mouse event (right-click)
   * @param data - Optional data to associate with menu (e.g., clicked item)
   */
  const openContextMenu = useCallback((event: React.MouseEvent | MouseEvent, data: T | null = null) => {
    event.preventDefault();
    event.stopPropagation();

    // If menu is already open at this position, close it (toggle behavior)
    if (contextMenu.isOpen && 
        contextMenu.position?.x === event.clientX && 
        contextMenu.position?.y === event.clientY) {
      setContextMenu({
        isOpen: false,
        position: null,
        data: null,
      });
      return;
    }

    setContextMenu({
      isOpen: true,
      position: {
        x: event.clientX,
        y: event.clientY,
      },
      data,
    });
  }, [contextMenu]);

  /**
   * Close context menu
   */
  const closeContextMenu = useCallback(() => {
    setContextMenu({
      isOpen: false,
      position: null,
      data: null,
    });
  }, []);

  /**
   * Handle item click
   * @param item - The clicked menu item
   */
  const handleItemClick = useCallback((item: any) => {
    // Item action is already executed by ContextMenu component
    // This callback is for additional handling if needed
    closeContextMenu();
  }, [closeContextMenu]);

  return {
    contextMenu,
    openContextMenu,
    closeContextMenu,
    handleItemClick
  };
};
